import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AdminLiveSessionService, AdminSession, AdminSessionDetail } from '@shared/services/live-sessions';
import { Subscription } from 'rxjs';
import { DrawerComponent } from '../../../@shared/components/drawer/drawer.component';

@Component({
  selector: 'app-session-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-drawer #drawer [closable]="true" [customClass]="'sm:max-w-md md:max-w-lg'" [alignment]="'right'" (closeDrawer)="onClose()">
      <div header class="px-6 py-4 border-b border-neutral-100">
        <h3 class="text-lg font-semibold text-neutral-800">
          @if (detail()) {
            <span i18n>Session</span> #{{ detail()!.id }}
          } @else {
            <span i18n>Session Details</span>
          }
        </h3>
      </div>
      <div content>
        @if (detail()) {
          <div class="p-6 space-y-6">
            <!-- Session Info -->
            <div class="space-y-3">
              <h4 class="text-sm font-medium text-neutral-500 uppercase tracking-wider" i18n>Session Info</h4>
              <div class="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span class="text-neutral-500" i18n>Course</span>
                  <p class="font-medium text-neutral-800">{{ detail()!.course_title }}</p>
                </div>
                <div>
                  <span class="text-neutral-500" i18n>Series</span>
                  <p class="font-medium text-neutral-800">{{ detail()!.series_name }}</p>
                </div>
                <div>
                  <span class="text-neutral-500" i18n>Instructor</span>
                  <p class="font-medium text-neutral-800">{{ detail()!.instructor_name }}</p>
                </div>
                <div>
                  <span class="text-neutral-500" i18n>Date/Time</span>
                  <p class="font-medium text-neutral-800">{{ detail()!.start_datetime | date:'MMM d, y, h:mm a' }}</p>
                </div>
                <div>
                  <span class="text-neutral-500" i18n>Duration</span>
                  <p class="font-medium text-neutral-800">{{ detail()!.duration }} min</p>
                </div>
                <div>
                  <span class="text-neutral-500" i18n>Status</span>
                  <p>
                    <span class="tag tag-sm rounded-lg" [class]="getStatusClass(detail()!.temporal_status)">
                      {{ getStatusLabel(detail()!.temporal_status) }}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <!-- Video Status -->
            <div class="space-y-3">
              <h4 class="text-sm font-medium text-neutral-500 uppercase tracking-wider" i18n>Video Status</h4>
              <div class="grid grid-cols-2 gap-3 text-sm">
                <div class="flex items-center gap-2">
                  @if (detail()!.is_video_active) {
                    <span class="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></span>
                    <span class="text-green-700" i18n>Live</span>
                  } @else {
                    <span class="w-2.5 h-2.5 bg-neutral-300 rounded-full"></span>
                    <span class="text-neutral-500" i18n>Not Live</span>
                  }
                </div>
                <div>
                  <span class="text-neutral-500" i18n>Instructor Joined</span>
                  <p class="font-medium"
                    [class.text-green-600]="detail()!.instructor_joined"
                    [class.text-red-600]="!detail()!.instructor_joined && detail()!.temporal_status === 'in_progress'"
                    [class.text-neutral-400]="!detail()!.instructor_joined && detail()!.temporal_status !== 'in_progress'">
                    {{ detail()!.instructor_joined ? 'Yes' : (detail()!.temporal_status === 'in_progress' ? 'No' : '-') }}
                  </p>
                </div>
                <div>
                  <span class="text-neutral-500" i18n>Enrolled</span>
                  <p class="font-medium text-neutral-800">{{ detail()!.enrolled_count }}{{ detail()!.max_participants ? ' / ' + detail()!.max_participants : '' }}</p>
                </div>
                <div>
                  <span class="text-neutral-500" i18n>Attendees</span>
                  <p class="font-medium text-neutral-800">{{ detail()!.actual_attendee_count ?? '-' }}</p>
                </div>
              </div>
            </div>

            <!-- Enrolled Students Mini List -->
            @if (detail()!.participants && detail()!.participants.length > 0) {
              <div class="space-y-3">
                <h4 class="text-sm font-medium text-neutral-500 uppercase tracking-wider" i18n>Enrolled Students</h4>
                <div class="space-y-2 max-h-48 overflow-y-auto">
                  @for (p of detail()!.participants; track p.id) {
                    <div class="flex items-center justify-between text-sm py-1 border-b border-neutral-100">
                      <span class="text-neutral-800">{{ p.student_name }}</span>
                      <span class="text-neutral-500 text-xs">{{ p.effective_status }}</span>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        } @else if (isLoading()) {
          <div class="flex items-center justify-center h-64">
            <p class="text-neutral-400" i18n>Loading session details...</p>
          </div>
        }
      </div>
      <div footer class="px-6 py-4 border-t border-neutral-200 mt-auto">
        @if (detail()) {
          <div class="flex items-center gap-3">
            <button (click)="navigateToDetail()" class="btn bg-blue-600 text-white text-sm hover:bg-blue-700 rounded px-4 py-2" i18n>
              View Full Details
            </button>
            @if (detail()!.is_video_active) {
              <button (click)="joinSession()" class="btn bg-green-600 text-white text-sm hover:bg-green-700 rounded px-4 py-2" i18n>
                Join Session
              </button>
            }
          </div>
        }
      </div>
    </app-drawer>
  `,
  imports: [DatePipe, DrawerComponent],
})
export class SessionDrawerComponent implements OnDestroy {
  private readonly adminService = inject(AdminLiveSessionService);
  private readonly router = inject(Router);

  @ViewChild('drawer') private drawer!: DrawerComponent;

  detail = signal<AdminSessionDetail | null>(null);
  isLoading = signal(false);
  private sessionId = 0;
  private subscriptions = new Subscription();

  openSession(session: AdminSession): void {
    this.sessionId = session.id;
    this.detail.set(null);
    this.isLoading.set(true);
    this.drawer.open();

    this.subscriptions.add(
      this.adminService.getAdminSessionDetail(session.id).subscribe({
        next: (data) => {
          this.detail.set(data);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      })
    );
  }

  navigateToDetail(): void {
    this.drawer.close();
    this.router.navigate(['/live-sessions/sessions', this.sessionId]);
  }

  joinSession(): void {
    this.subscriptions.add(
      this.adminService.adminJoinSession(this.sessionId).subscribe({
        next: (res) => {
          sessionStorage.setItem('admin_rtk_token', res.auth_token);
          sessionStorage.setItem('admin_rtk_room', res.room_name);
          window.open(`/live-sessions/meeting/${this.sessionId}`, '_blank');
        },
      })
    );
  }

  onClose(): void {
    this.detail.set(null);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'upcoming': return 'tag-blue';
      case 'in_progress': return 'tag-green';
      case 'completed': return 'tag-neutral';
      case 'cancelled': return 'tag-red';
      default: return 'tag-neutral';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'upcoming': return 'Upcoming';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
